/**
 * Remote images (e.g. LinkedIn article covers) are often 0.5–1 MB PNG/JPEG.
 * Route them through the free wsrv.nl image CDN to get resized WebP/AVIF,
 * which cuts blog-list payload by ~90% and improves mobile LCP.
 * Local/relative assets are returned untouched (already optimized at build).
 */
export function optimizedImage(url: string | null | undefined, width = 640, quality = 72): string {
  if (!url) return "";
  if (!/^https?:\/\//i.test(url)) return url;
  try {
    // Must stay window-free: this runs during server rendering too, and the
    // SSR markup has to match what the browser renders after hydration.
    const parsed = new URL(url);
    if (parsed.hostname.endsWith("agapeco.net") || parsed.hostname === "wsrv.nl") return url;
  } catch {
    return url;
  }
  const src = url.replace(/^https?:\/\//i, "");
  return `https://wsrv.nl/?url=${encodeURIComponent(src)}&w=${width}&q=${quality}&output=webp&we`;
}

/**
 * Category accent colours are chosen for light surfaces; on the dark theme they
 * fail WCAG AA. Lift the lightness so badge text stays readable.
 */
export function readableAccent(hex?: string | null, minLightness = 72): string {
  if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return "hsl(var(--primary))";
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (d !== 0) {
    if (max === r) h = 60 * (((g - b) / d) % 6);
    else if (max === g) h = 60 * ((b - r) / d + 2);
    else h = 60 * ((r - g) / d + 4);
  }
  if (h < 0) h += 360;
  return `hsl(${Math.round(h)} ${Math.round(Math.max(s, 0.5) * 100)}% ${Math.max(Math.round(l * 100), minLightness)}%)`;
}
